import React from "react"
import { Box, Chip, Link, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { DS_REPRICING_ROUTES } from "@constants/routes"
import { dsColorNew } from "@palette"
import { repricingPass } from "@signals"
import { toneChipSx } from "Pages/DsRepricing/@components/tones"
import {
	KIND_LABELS, KIND_META, activityHref, cellActivityHref, iconOf, isSim, limitOf, markOf, priceLine, referenceOf,
	resultOf, sortDecisions, tetherOf,
} from "../Table/columns/Data/@services/cellDecisions"
import { cellPlace } from "../Table/columns/Data/@hooks/useCellDecisions"

/**
 * "Repricing decisions (N)" in the offer's detail (CU-869fmm949, criteria 4-7, UX Obs. 13, 15): one line per
 * decision — kind, ACRISS, strategy, before → after per day, result; the limit or the tether parent; the reference
 * competitor, marked when it is this offer; "Open in Activity". Then "+ N not priced".
 */
export default function PopoverDecisions({ cell, priceData }) {
	const decisions = sortDecisions(cell?.decisions)
	if (!decisions.length && !cell?.notPriced) return null
	const place = cellPlace(priceData)
	return <Box sx={{ px: 1.75, py: 1.25, borderTop: `1px solid ${dsColorNew.grayBorder}` }}>
		<Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
			<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle }}>
				Repricing decisions ({decisions.length})
			</Typography>
			{decisions.length > 1 && <Link href={cellActivityHref({
				executionId: repricingPass.value, office: decisions[0]?.scope?.office, site: place.site, date: place.date, lor: place.lor,
			}, DS_REPRICING_ROUTES.ACTIVITY_PATH)}
				target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12 }}>All in Activity</Link>}
		</Box>
		<Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
			{decisions.map(d => <DecisionLine key={d.id} d={d} priceData={priceData} lor={place.lor}/>)}
		</Box>
		{!!cell?.notPriced && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.75 }}>
			+ {cell.notPriced} not priced (no strategy / no rate card)
		</Typography>}
	</Box>
}

function DecisionLine({ d, priceData, lor }) {
	const mark = markOf([d])
	const kindLabel = `${KIND_LABELS[d.event_type] || d.event_type}${isSim(d) ? ' · SIM' : ''}`
	const result = resultOf(d)
	const limit = limitOf(d)
	const tether = tetherOf(d)
	const reference = referenceOf(d, priceData, '€', lor)
	const prices = priceLine(d)
	return <Box sx={{ fontSize: 12, color: dsColorNew.textDark }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
			<Chip size="small" icon={<IconSeeker icon={iconOf(mark)} sx={{ fontSize: 10, ml: '6px !important' }}/>} label={kindLabel}
				sx={{ ...toneChipSx(KIND_META[d.event_type]?.tone), height: 20, ...(isSim(d) && { borderStyle: 'dashed' }) }}/>
			<Typography component="code" sx={{ fontSize: 12, fontWeight: 600 }}>{d.scope?.sipp || '—'}</Typography>
			<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.strategy_name}</Typography>
			{!!result && <Chip size="small" label={result.label} sx={{ ...toneChipSx(result.tone), height: 20, ml: 'auto' }}/>}
		</Box>
		{!!prices && <Typography sx={{ fontSize: 12, mt: 0.25 }}>{prices}</Typography>}
		{!!limit && <Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber }}>Limit: {limit}</Typography>}
		{!!tether && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{tether}</Typography>}
		{d.event_type === 'quarantined' && !!d.note && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>Held: {d.note}</Typography>}
		{!!reference && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
			{reference.text}{reference.isThisOffer && <b> · this offer was the reference</b>}
		</Typography>}
		<Link href={activityHref(d, DS_REPRICING_ROUTES.ACTIVITY_PATH)} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12 }}>Open in Activity</Link>
	</Box>
}
